(() => {
  "use strict";

  /*
   * KOURITEN COUNTDOWN — CART STATE SYNC
   *
   * RULES
   *   CURRENT + ALIVE -> KEEP
   *   STALE + ALIVE   -> UPDATE existing cart line
   *   EXPIRED         -> DO NOT update
   *
   * IMPORTANT
   *   - Uses existing Shopify cart line key.
   *   - Preserves all existing line properties.
   *   - Never changes quantity.
   *   - Shopify recalculates current cart state/price.
   *   - Processes ONE stale line at a time.
   *   - Re-reads the cart after every mutation.
   */

  const TOKEN_KEY = "_kouriten_token";
  const LINE_SELECTOR = '[data-kouriten-cart-line="true"]';
  const NOTICE_CLASS = "kouriten-cart-sync-notice";
  const EXPIRED_NOTICE_CLASS = "kouriten-cart-expired-notice";

  let syncInFlight = false;
  let syncQueued = false;
  let initialized = false;
  let noticeShown = false;

  function getCartUrl() {
    return (typeof routes !== "undefined" && routes.cart_url) || "/cart";
  }

  function getCartChangeUrl() {
    return (
      (typeof routes !== "undefined" && routes.cart_change_url) ||
      "/cart/change.js"
    );
  }

  function getLineElements() {
    return Array.from(document.querySelectorAll(LINE_SELECTOR));
  }

  function getLineElementByKey(key) {
    for (const element of getLineElements()) {
      const lineKey = element.querySelector(
        "[data-quantity-line-key]"
      )?.dataset.quantityLineKey;

      if (lineKey === key) {
        return element;
      }
    }

    return null;
  }

  function showNotice() {
    if (noticeShown) return;

    noticeShown = true;

    const message =
      "限時預購商品已更新，購物車內的商品資料已更新至最新版本，請於結帳前確認價格、內容及貨期。";

    const targets = [];

    const mainCart = document
      .querySelector("#main-cart-items")
      ?.closest("cart-items");

    if (mainCart) {
      targets.push({
        root: mainCart,
        before: mainCart.querySelector("form#cart"),
      });
    }

    const drawer = document.querySelector("cart-drawer");

    if (drawer) {
      targets.push({
        root: drawer,
        before: drawer.querySelector("cart-drawer-items"),
      });
    }

    targets.forEach(({ root, before }) => {
      let notice = root.querySelector(`.${NOTICE_CLASS}`);

      if (!notice) {
        notice = document.createElement("div");
        notice.className = NOTICE_CLASS;
        notice.setAttribute("role", "status");
        notice.setAttribute("aria-live", "polite");

        if (before) {
          before.parentNode.insertBefore(notice, before);
        } else {
          root.prepend(notice);
        }
      }

      notice.textContent = message;
      notice.hidden = false;
    });
  }

  function clearExpiredLineNotices() {
    document
      .querySelectorAll(".kouriten-cart-line-expired-notice")
      .forEach((notice) => notice.remove());

    document
      .querySelectorAll(".kouriten-cart-line-expired")
      .forEach((element) => {
        element.classList.remove(
          "kouriten-cart-line-expired"
        );
      });
  }

  function showExpiredLineNotice(element) {
    if (!element) {
      return;
    }

    if (
      element.querySelector(
        ".kouriten-cart-line-expired-notice"
      )
    ) {
      return;
    }

    const notice = document.createElement("div");

    notice.className =
      "kouriten-cart-line-expired-notice";

    notice.setAttribute("role", "alert");

    notice.textContent =
      "此商品的限時預購已結束，請移除商品後再結帳。";

    const title =
      element.querySelector(".cart-item__title");

    if (title) {
      title.insertAdjacentElement(
        "afterend",
        notice
      );
    } else {
      const details =
        element.querySelector(".cart-item__details");

      if (details) {
        details.prepend(notice);
      }
    }

    element.classList.add(
      "kouriten-cart-line-expired"
    );
  }

  function showExpiredNotice() {
    const message =
      "限時預購已結束，請先移除相關商品後再結帳。";

    const targets = [];

    const mainCart = document
      .querySelector("#main-cart-items")
      ?.closest("cart-items");

    if (mainCart) {
      targets.push({
        root: mainCart,
        before: mainCart.querySelector("form#cart"),
      });
    }

    const drawer = document.querySelector("cart-drawer");

    if (drawer) {
      targets.push({
        root: drawer,
        before: drawer.querySelector("cart-drawer-items"),
      });
    }

    targets.forEach(({ root, before }) => {
      let notice = root.querySelector(
        `.${EXPIRED_NOTICE_CLASS}`
      );

      if (!notice) {
        notice = document.createElement("div");
        notice.className =
          `${NOTICE_CLASS} ${EXPIRED_NOTICE_CLASS}`;
        notice.setAttribute("role", "alert");

        if (before) {
          before.parentNode.insertBefore(notice, before);
        } else {
          root.prepend(notice);
        }
      }

      notice.textContent = message;
      notice.hidden = false;
    });
  }

  async function fetchCart() {
    const response = await fetch(`${getCartUrl()}.js`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    return response.json();
  }

  function hasExpiredCountdownLine(cart) {

    let hasExpired = false;

    clearExpiredLineNotices();

    for (const item of cart.items || []) {

      const token = item.properties?.[TOKEN_KEY];

      if (!token) {

        continue;

      }

      const element = getLineElementByKey(item.key);

      if (!element) {

        continue;

      }

      const enabled =

        element.dataset.kouritenCountdownEnabled === "true";

      const endAt =

        element.dataset.kouritenCountdownEndAt || "";

      const endMs = endAt ? Date.parse(endAt) : NaN;

      const expired =

        enabled &&

        Number.isFinite(endMs) &&

        endMs <= Date.now();

      if (expired) {

        hasExpired = true;

        showExpiredLineNotice(element);

      }

    }

    return hasExpired;

  }

  function findFirstStaleAliveLine(cart) {
    for (const item of cart.items || []) {
      const oldToken = item.properties?.[TOKEN_KEY];

      if (!oldToken) {
        continue;
      }

      const element = getLineElementByKey(item.key);

      if (!element) {
        continue;
      }

      const currentToken =
        element.dataset.kouritenCurrentToken || "";

      const enabled =
        element.dataset.kouritenCountdownEnabled === "true";

      const endAt =
        element.dataset.kouritenCountdownEndAt || "";

      const endMs = endAt ? Date.parse(endAt) : NaN;

      const alive =
        enabled &&
        Number.isFinite(endMs) &&
        endMs > Date.now();

      if (!currentToken) {
        continue;
      }

      if (!alive) {
        continue;
      }

      if (oldToken === currentToken) {
        continue;
      }

      return {
        item,
        element,
        currentToken,
        endAt,
      };
    }

    return null;
  }

  async function syncOneStaleAliveLine() {
    if (syncInFlight) {
      syncQueued = true;
      return;
    }

    syncInFlight = true;
    syncQueued = false;

    try {
      /*
       * Always read the latest cart before selecting a line.
       */
      const cart = await fetchCart();

      if (hasExpiredCountdownLine(cart)) {
        showExpiredNotice();
      }

      const candidate = findFirstStaleAliveLine(cart);

      /*
       * No stale/alive line.
       * Expired lines are deliberately ignored.
       */
      if (!candidate) {
        return;
      }

      const {
        item,
        currentToken,
        endAt,
      } = candidate;

      /*
       * Preserve EVERY existing line property.
       * Only replace _kouriten_token.
       */
      const properties = {
        ...(item.properties || {}),
        [TOKEN_KEY]: currentToken,
      };

      const response = await fetch(getCartChangeUrl(), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties,
        }),
      });

      const state = await response.json();

      if (!response.ok || state.errors) {
        console.error(
          "Cart synchronization failed",
          state
        );
        return;
      }

      /*
       * Shopify may merge the line or generate a new key.
       * Do NOT assume the previous line still exists.
       *
       * Re-read the cart on the next pass.
       */

      /*
       * Refresh Dawn's Cart Page / Cart Drawer immediately.
       *
       * The server cart has already been updated by /cart/change.js.
       * Publish the fresh cart state so Dawn re-renders the UI
       * without requiring the customer to refresh the page.
       *
       * The source marker prevents our own subscriber from
       * starting another sync cycle.
       */
      const finalCart = await fetchCart();

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "kouriten-cart-sync",
          cartData: finalCart,
        });
      }

      showNotice();

      /*
       * Re-run after the mutation so the cart is evaluated
       * from its newest server state.
       */
      window.setTimeout(() => {
        syncOneStaleAliveLine();
      }, 100);

    } catch (error) {
      console.error(
        "Cart synchronization error",
        error
      );
    } finally {
      syncInFlight = false;

      if (syncQueued) {
        syncQueued = false;

        window.setTimeout(() => {
          syncOneStaleAliveLine();
        }, 100);
      }
    }
  }

  function scheduleSync(delay = 250) {
    window.setTimeout(() => {
      syncOneStaleAliveLine();
    }, delay);
  }

  function init() {
    if (initialized) {
      return;
    }

    initialized = true;

    if (!document.querySelector("cart-drawer, cart-items")) {
      return;
    }

    scheduleSync(0);

    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        /*
         * Ignore our own published cart update.
         */
        if (event?.source === "kouriten-cart-sync") {
          return;
        }

        scheduleSync(250);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
